import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { Button, IconButton, Input, Card, Badge, Skeleton, Checkbox, RadioGroup, Select, Stepper, Tabs, StatusBadge, ChannelBadge, PriceDisplay, Avatar, Dialog, Toast, Rating, Eyebrow, Diamond, Photo } from '@/components/ui';

// Dev-only component gallery for browser-smoking the UI kit. notFound() in prod
// so it is never a public route on book.ollio.place.
export const Route = createFileRoute('/gallery')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  component: Gallery,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-b border-border py-8">
      <h2 className="font-serif text-2xl text-[var(--text-heading)]">{title}</h2>
      <div className="flex flex-wrap items-center gap-4">{children}</div>
    </section>
  );
}

// Gallery-only no-op dismiss — keeps ESLint happy (no-empty-function)
function noop() { /* gallery dismiss stub */ }

function Gallery() {
  const [checked, setChecked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="font-serif text-4xl text-[var(--text-heading)]">UI Kit — R2d</h1>

      <Section title="Button — variants">
        <Button variant="primary">Primary</Button>
        <Button variant="gold">Gold</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="destructive">Destructive</Button>
        <Button loading>Loading</Button>
        <Button disabled>Disabled</Button>
        <Button variant="gold" pill>Pill</Button>
      </Section>

      <Section title="Button — sizes">
        <Button size="sm">Small</Button>
        <Button size="md">Medium</Button>
        <Button size="lg">Large</Button>
      </Section>

      <Section title="IconButton">
        <IconButton label="Search" variant="ghost"><Search className="h-5 w-5" /></IconButton>
        <IconButton label="Search" variant="solid"><Search className="h-5 w-5" /></IconButton>
        <IconButton label="Close" variant="outline" round><X className="h-5 w-5" /></IconButton>
      </Section>

      <Section title="Input">
        <div className="flex w-full max-w-sm flex-col gap-4">
          <Input label="Email" placeholder="you@example.com" hint="We'll send a sign-in link." />
          <Input label="Full name" required leadingIcon={<Search />} placeholder="With icon" />
          <Input label="Broken" error="This field is required." defaultValue="bad" />
          <Input label="Notes" multiline placeholder="Multiline…" />
          <Input label="Disabled" disabled placeholder="Disabled" />
        </div>
      </Section>

      <Section title="Badge">
        {(['neutral', 'navy', 'gold', 'success', 'warning', 'danger', 'info', 'solid'] as const).map((v) => (
          <Badge key={v} variant={v}>{v}</Badge>
        ))}
        <Badge variant="success" dot>With dot</Badge>
        <Badge variant="navy" square>Square</Badge>
      </Section>

      <Section title="Card">
        <Card variant="elevated" className="w-64"><h3 className="font-serif text-lg">Elevated</h3><p className="text-sm text-muted">Default surface.</p></Card>
        <Card variant="outline" className="w-64"><h3 className="font-serif text-lg">Outline</h3><p className="text-sm text-muted">Hairline border.</p></Card>
        <Card variant="warm" interactive className="w-64"><h3 className="font-serif text-lg">Warm + interactive</h3><p className="text-sm text-muted">Hover me.</p></Card>
      </Section>

      <Section title="Skeleton">
        <div className="flex w-full max-w-sm flex-col gap-3">
          <Skeleton shape="rect" height={140} />
          <Skeleton width="60%" />
          <Skeleton width="40%" />
          <Skeleton shape="circle" width={48} height={48} />
        </div>
      </Section>

      <Section title="Checkbox">
        <Checkbox />
        <Checkbox defaultChecked />
        <Checkbox indeterminate />
        <Checkbox disabled />
        <Checkbox
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          label="Remember my details"
          description="We'll pre-fill your info next time."
        />
      </Section>

      <Section title="RadioGroup">
        <RadioGroup
          options={['Economy', 'Standard', 'Premium']}
          defaultValue="Standard"
        />
        <RadioGroup
          options={['Mon', 'Tue', 'Wed', 'Thu', 'Fri']}
          defaultValue="Mon"
          row
        />
      </Section>

      <Section title="Select">
        <div className="w-56">
          <Select
            label="Apartment type"
            placeholder="Choose one…"
            options={['Studio', '1-bedroom', '2-bedroom', 'Penthouse']}
          />
        </div>
      </Section>

      <Section title="Stepper">
        <div className="w-full">
          <Stepper steps={['Dates', 'Details', 'Payment', 'Done']} current={1} />
        </div>
      </Section>

      <Section title="Tabs">
        <div className="flex w-full flex-col gap-6">
          <Tabs
            variant="underline"
            tabs={[
              { value: 'overview', label: 'Overview' },
              { value: 'bookings', label: 'Bookings', count: 12 },
              { value: 'messages', label: 'Messages' },
            ]}
            defaultValue="bookings"
          />
          <Tabs
            variant="pill"
            tabs={[
              { value: 'all', label: 'All', count: 24 },
              { value: 'active', label: 'Active' },
              { value: 'archived', label: 'Archived' },
            ]}
            defaultValue="all"
          />
        </div>
      </Section>

      <Section title="StatusBadge">
        <StatusBadge status="confirmed" />
        <StatusBadge status="pending" />
        <StatusBadge status="cancelled" />
        <StatusBadge status="checked-in" />
        <StatusBadge status="inquiry" />
        <StatusBadge status="cleaning" />
        <StatusBadge status="checked-out" />
        <StatusBadge status="no-show" />
      </Section>

      <Section title="ChannelBadge">
        <ChannelBadge channel="direct" />
        <ChannelBadge channel="booking" />
        <ChannelBadge channel="airbnb" />
        <ChannelBadge channel="direct" bare />
        <ChannelBadge channel="booking" square />
        <ChannelBadge channel="airbnb" square />
      </Section>

      <Section title="PriceDisplay">
        <PriceDisplay amount={89} size="sm" />
        <PriceDisplay amount={89} size="md" />
        <PriceDisplay amount={89} size="lg" />
        <PriceDisplay amount={89} per="night" />
        <PriceDisplay amount={89} was={120} per="night" />
        <PriceDisplay amount={8900} currency="RSD" per="night" />
      </Section>

      <Section title="Avatar">
        <Avatar name="Ana Petrović" size="xs" />
        <Avatar name="Ivan Novak" size="sm" />
        <Avatar name="Maria Santos" size="md" />
        <Avatar name="Ollio Guest" size="lg" />
        <Avatar name="Emma Wilson" size="xl" />
        <Avatar name="Ring User" ring />
        <Avatar name="Online Now" status="online" />
        <Avatar name="Away Now" status="away" />
        <Avatar name="Offline" status="offline" />
      </Section>

      <Section title="Toast">
        <div className="flex flex-col gap-3 max-w-md">
          <Toast kind="success" title="Booking confirmed" message="Your reservation at Studio A has been confirmed." onClose={noop} />
          <Toast kind="error" title="Payment failed" message="We couldn't process your card. Please try again." onClose={noop} />
          <Toast kind="warning" title="Check-in soon" message="Guest arrives in 2 hours — apartment not yet marked clean." />
          <Toast kind="info" title="New message" message="Ana Petrović sent you a message about their upcoming stay." onClose={noop} />
          <Toast kind="default" title="Reminder" message="Rate plan update scheduled for tomorrow." />
        </div>
      </Section>

      <Section title="Dialog">
        <Button variant="primary" onClick={() => setDialogOpen(true)}>Open Dialog</Button>
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Confirm cancellation"
          description="This action cannot be undone. The booking will be cancelled and the guest notified."
          footer={
            <>
              <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => setDialogOpen(false)}>Confirm</Button>
            </>
          }
        >
          <p className="text-sm text-muted">Booking #1234 — Studio A, 3 nights from 20 Jun 2026.</p>
        </Dialog>
      </Section>

      <Section title="Rating">
        <Rating value={4.96} reviews={128} />
        <Rating value={4.9} size={18} />
      </Section>

      <Section title="Eyebrow">
        <Eyebrow>Featured Stay</Eyebrow>
        <div className="bg-primary p-3 rounded-lg">
          <Eyebrow onDark>On Dark</Eyebrow>
        </div>
      </Section>

      <Section title="Diamond">
        <span className="inline-flex items-center gap-2 text-sm">Direct <Diamond /> Booking.com <Diamond /> Airbnb</span>
      </Section>

      <Section title="Photo">
        <div className="h-40 w-64">
          <Photo label="Old Town" />
        </div>
        <div className="h-40 w-64">
          <Photo dim={0.4} label="Dimmed overlay" />
        </div>
        <div className="h-40 w-64">
          <Photo seed={1} label="Gradient seed 1" />
        </div>
      </Section>
    </div>
  );
}
